<template>
  <div>
    <portal to="contact-merge-header">
      <h3 class="text-lg font-medium text-center">Compare Contacts</h3>
      <p class="text-sm mt-2 text-center font-normal text-gray-600 measure mx-auto">
        When you merge, the master record is updated with the values you choose, and relationships
        to other items are transferred to the master record.
      </p>
    </portal>

    <table class="w-full">
      <thead>
        <!-- master record selector row -->
        <tr>
          <th
            class="uppercase font-medium text-gray-600 px-4 sm:px-6 py-3 border-r border-b border-gray-200"
          />
          <th
            v-for="contact in selectedContacts"
            :key="contact.id"
            class="px-4 sm:px-6 py-3 font-medium border-b border-gray-200 text-left"
          >
            {{ contact.fullName }}
          </th>
        </tr>
      </thead>
      <tbody>
        <!-- master record selector row -->
        <tr class="odd:bg-gray-50">
          <td
            class="uppercase font-medium text-gray-600 px-4 sm:px-6 py-3 border-r border-gray-200"
          >
            Master Record
          </td>
          <td v-for="contact in contacts" :key="contact.id" class="px-4 sm:px-6 py-3">
            <input
              type="radio"
              name="masterContactId"
              :value="contact.id"
              v-model="masterContactId"
            />
            Use as master
          </td>
        </tr>

        <template v-for="field in fieldsToChoose">
          <tr
            v-if="!showOnlyFieldsWithDifferences || fieldsWithDifferences.indexOf(field) > -1"
            :key="field"
            class="odd:bg-gray-50"
          >
            <td
              class="uppercase font-medium text-gray-600 px-4 sm:px-6 py-3 border-r border-gray-200"
            >
              {{ labelByField[field] || field }}
            </td>
            <td
              v-for="contact in contacts"
              :key="`${field}_${contact.id}`"
              class="px-4 sm:px-6 py-3"
            >
              <input
                v-if="fieldsWithDifferences.indexOf(field) > -1"
                type="radio"
                :name="field"
                :value="contact[field]"
                :checked="formData[field] === contact[field]"
                @change="updateFormDataAttribute(field, $event.target.value)"
              />
              {{ contact[field] || `field '${field}' not found` }}
            </td>
          </tr>
        </template>

        <tr>
          <td
            class="uppercase font-medium text-gray-600 px-4 sm:px-6 py-3 border-t border-gray-200 text-center"
            :colspan="contacts.length + 1"
          >
            <BaseButton
              primary
              outline
              @click="showOnlyFieldsWithDifferences = !showOnlyFieldsWithDifferences"
            >
              {{
                showOnlyFieldsWithDifferences
                  ? 'Show all fields'
                  : 'Show only fields with differences'
              }}
            </BaseButton>
          </td>
        </tr>
      </tbody>
      <tbody class="border-t">
        <template v-for="field in metaFields">
          <tr :key="field" class="odd:bg-gray-50">
            <td
              class="uppercase font-medium text-gray-600 px-4 sm:px-6 py-3 border-r border-gray-200"
            >
              {{ labelByField[field] || field }}
            </td>
            <td
              v-for="contact in contacts"
              :key="`${field}_${contact.id}`"
              class="px-4 sm:px-6 py-3"
            >
              {{ contact[field] || `field '${field}' not found` }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <portal to="contact-merge-back">
      <template #default="{ prevStep, hasPrevStep }">
        <BaseButton :disabled="!hasPrevStep" ghost @click="prevStep"> Back </BaseButton>
      </template>
    </portal>

    <portal to="contact-merge-next">
      <template #default="{ nextStep, hasNextStep }">
        <BaseButton :disabled="!hasNextStep" primary @click="nextStep"> Next </BaseButton>
      </template>
    </portal>
  </div>
</template>

<script>
import moment from 'moment';
import { cloneDeep, pick, sortBy, uniqBy } from 'lodash-es';

export default {
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hiddenFields: ['id'],
      fieldsToChoose: ['firstName', 'lastName', 'primaryEmailAddress', 'primaryPhoneNumber'],
      metaFields: ['createdAt', 'updatedAt'],
      labelByField: {
        firstName: 'First name',
        lastName: 'Last name',
        primaryEmailAddress: 'Primary email address',
        primaryPhoneNumber: 'Primary phone number',
        createdAt: 'Date created',
        updatedAt: 'Last modified date',
      },
      transformByField: {
        primaryEmailAddress: this.getPrimaryEmailAddressById,
        primaryPhoneNumber: this.getPrimaryPhoneNumberById,
        createdAt: this.formatDate,
        updatedAt: this.formatDate,
      },
      showOnlyFieldsWithDifferences: true,
      contacts: [],
    };
  },
  computed: {
    selectedContacts() {
      return this.$store.getters[`${this.accountId}:contactMerge/selectedContacts`];
    },
    fieldsWithDifferences() {
      const contacts = cloneDeep(this.contacts);
      return this.fieldsToChoose.filter((field) => uniqBy(contacts, field).length > 1);
    },
    masterContactId: {
      get() {
        return this.$store.getters[`${this.accountId}:contactMerge/masterContactId`];
      },
      set(contactId) {
        this.$store.commit(`${this.accountId}:contactMerge/setMasterContactId`, contactId);
      },
    },
    formData: {
      get() {
        return this.$store.getters[`${this.accountId}:contactMerge/formData`];
      },
      set(formData) {
        this.$store.commit(`${this.accountId}:contactMerge/setFormData`, formData);
      },
    },
  },
  created() {
    this.prepareContacts();

    this.masterContactId = this.contacts?.[0]?.id || null;
    this.formData = cloneDeep(pick(this.contacts?.[0], [...this.fieldsToChoose]));
  },
  methods: {
    prepareContacts() {
      this.contacts = this.sortContactsByOldestAscending(this.selectedContacts)
        .map(this.pickMinimalFields)
        .map(this.transformFields);
    },
    sortContactsByOldestAscending(contacts) {
      return sortBy(cloneDeep(contacts), [(contact) => contact.createdAt]);
    },
    pickMinimalFields(contact) {
      return pick(contact, [...this.hiddenFields, ...this.metaFields, ...this.fieldsToChoose]);
    },
    transformFields(contact) {
      return Object.keys(contact).reduce((fields, field) => {
        const value = contact[field];

        return {
          ...fields,
          [field]: field in this.transformByField ? this.transformByField[field](value) : value,
        };
      }, {});
    },
    formatDate(date) {
      return moment.utc(date).format('D/M/YYYY h:mm A');
    },
    getPrimaryEmailAddressById(emailAddressId) {
      const primaryEmailAddress = this.$store.state.entities.emailAddresses.byId[emailAddressId];

      return primaryEmailAddress?.address || '';
    },
    getPrimaryPhoneNumberById(phoneNumberId) {
      const primaryPhoneNumber = this.$store.state.entities.phoneNumbers.byId[phoneNumberId];

      return primaryPhoneNumber?.number || '';
    },
    updateFormDataAttribute(attribute, value) {
      this.$store.commit(`${this.accountId}:contactMerge/setFormDataAttribute`, {
        attribute,
        value,
      });
    },
  },
};
</script>
